import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { I18nProvider } from "@/context/I18nContext";
import Layout from "@/components/Layout";
import AdminLayout from "@/components/AdminLayout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Subscribe from "@/pages/Subscribe";
import Dashboard from "@/pages/Dashboard";
import Properties from "@/pages/Properties";
import PropertyDetail from "@/pages/PropertyDetail";
import Tenants from "@/pages/Tenants";
import Contracts from "@/pages/Contracts";
import Payments from "@/pages/Payments";
import PaymentDetail from "@/pages/PaymentDetail";
import Expenses from "@/pages/Expenses";
import Simulator from "@/pages/Simulator";
import Documents from "@/pages/Documents";
import Legal from "@/pages/Legal";
import Market from "@/pages/Market";
import FinanceAI from "@/pages/FinanceAI";
import DealAnalyzer from "@/pages/DealAnalyzer";
import PortfolioOptimizer from "@/pages/PortfolioOptimizer";
import Advisor from "@/pages/Advisor";
import TaxAssistant from "@/pages/TaxAssistant";
import Marketplace from "@/pages/Marketplace";
import Settings from "@/pages/Settings";
import Admin from "@/pages/Admin";
import AdminPlans from "@/pages/AdminPlans";
import Accountant from "@/pages/Accountant";
import AccountantClients from "@/pages/AccountantClients";
import AccountantClientDetail from "@/pages/AccountantClientDetail";
import AcceptAccountantInvite from "@/pages/AcceptAccountantInvite";

function Protected({ children, adminOnly }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 rounded-full border-2 border-foreground border-t-transparent animate-spin" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  // Accountant accounts only ever see /accountant/clients/* (read-only client views)
  // and /settings (their own 2FA) — everything else redirects there, and vice versa.
  const isAccountant = user.role === "accountant";
  const inAccountantArea = location.pathname.startsWith("/accountant/clients");
  if (isAccountant && !inAccountantArea && location.pathname !== "/settings") {
    return <Navigate to="/accountant/clients" replace />;
  }
  if (!isAccountant && inAccountantArea) return <Navigate to="/dashboard" replace />;
  // No free tier: a normal user without an active subscription is walled off to
  // /subscribe (card + 14-day trial) — everything else redirects there, and a
  // subscribed user never sees the wall. Admin/accountant always have access.
  const needsSubscription = user.role === "user" && !user.has_app_access;
  // Stripe Checkout always returns to /settings?checkout=... — the cached user
  // here is still the pre-checkout one (has_app_access false), so let this one
  // request through even though needsSubscription looks true. Settings.js's own
  // effect calls refreshUser() and only strips the query string once that
  // resolves, so by the time this guard re-evaluates without the bypass,
  // has_app_access is already current.
  const isCheckoutReturn = location.pathname === "/settings" && new URLSearchParams(location.search).has("checkout");
  if (needsSubscription && location.pathname !== "/subscribe" && !isCheckoutReturn) {
    return <Navigate to="/subscribe" replace />;
  }
  if (!needsSubscription && location.pathname === "/subscribe") {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/accountant/accept" element={<AcceptAccountantInvite />} />
      <Route path="/subscribe" element={<Protected><Subscribe /></Protected>} />
      <Route
        element={
          <Protected>
            <Layout />
          </Protected>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/properties" element={<Properties />} />
        <Route path="/properties/:id" element={<PropertyDetail />} />
        <Route path="/tenants" element={<Tenants />} />
        <Route path="/contracts" element={<Contracts />} />
        <Route path="/payments" element={<Payments />} />
        <Route path="/payments/:id" element={<PaymentDetail />} />
        <Route path="/expenses" element={<Expenses />} />
        <Route path="/simulator" element={<Simulator />} />
        <Route path="/documents" element={<Documents />} />
        <Route path="/legal" element={<Legal />} />
        <Route path="/market" element={<Market />} />
        <Route path="/finance-ai" element={<FinanceAI />} />
        <Route path="/deal-analyzer" element={<DealAnalyzer />} />
        <Route path="/portfolio-optimizer" element={<PortfolioOptimizer />} />
        <Route path="/advisor" element={<Advisor />} />
        <Route path="/tax-report" element={<TaxAssistant />} />
        <Route path="/marketplace" element={<Marketplace />} />
        <Route path="/accountant" element={<Accountant />} />
        <Route path="/accountant/clients" element={<AccountantClients />} />
        <Route path="/accountant/clients/:clientId" element={<AccountantClientDetail />} />
        <Route path="/settings" element={<Settings />} />
      </Route>
      <Route
        element={
          <Protected adminOnly>
            <AdminLayout />
          </Protected>
        }
      >
        <Route path="/admin" element={<Admin />} />
        <Route path="/admin/plans" element={<AdminPlans />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <div className="App">
              <AppRoutes />
            </div>
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </AuthProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}
